import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  getWeatherDashboardAreaSummary,
  getWeatherDashboardIncident,
  getWeatherDashboardRiskRanking,
  getWeatherDashboardSummary,
  getWeatherDashboardTimeline,
  setMapFocusHint,
} from "../../services/midpointApi";

const WINDOW_OPTIONS = [
  { value: "30_MIN", label: "30 min" },
  { value: "1_HOUR", label: "1 hour" },
  { value: "3_HOUR", label: "3 hours" },
  { value: "6_HOUR", label: "6 hours" },
  { value: "12_HOUR", label: "12 hours" },
  { value: "24_HOUR", label: "24 hours" },
];

const DISCLAIMER =
  "Midpoint-generated risk signal — not an official emergency warning.";

function formatTime(value) {
  if (!value) {
    return "—";
  }
  try {
    return new Date(value).toLocaleString();
  } catch {
    return value;
  }
}

function riskLevelClass(level) {
  return `risk-level risk-level--${String(level || "unknown").toLowerCase()}`;
}

const WeatherIntelligenceDashboardPage = ({ onClose, onOpenMap }) => {
  const [windowCode, setWindowCode] = useState("3_HOUR");
  const [municipality, setMunicipality] = useState("");
  const [includeSample, setIncludeSample] = useState(true);
  const [sampleOnly, setSampleOnly] = useState(false);
  const [timelineEventFilter, setTimelineEventFilter] = useState("");
  const [summary, setSummary] = useState(null);
  const [timeline, setTimeline] = useState(null);
  const [ranking, setRanking] = useState(null);
  const [areaSummary, setAreaSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");
  const [activeList, setActiveList] = useState(null);
  const [incident, setIncident] = useState(null);
  const [incidentLoading, setIncidentLoading] = useState(false);
  const debounceRef = useRef(null);

  const queryParams = useMemo(
    () => ({
      window: windowCode,
      municipality: municipality.trim() || undefined,
      includeSample,
      sampleOnly,
    }),
    [windowCode, municipality, includeSample, sampleOnly]
  );

  const loadDashboard = useCallback(async () => {
    try {
      setLoading(true);
      setErrorMessage("");
      const [summaryData, timelineData, rankingData] = await Promise.all([
        getWeatherDashboardSummary(queryParams),
        getWeatherDashboardTimeline({
          ...queryParams,
          limit: 100,
          eventTypes: timelineEventFilter || undefined,
        }),
        getWeatherDashboardRiskRanking({ ...queryParams, limit: 10 }),
      ]);
      setSummary(summaryData);
      setTimeline(timelineData);
      setRanking(rankingData);

      if (municipality.trim()) {
        const area = await getWeatherDashboardAreaSummary(queryParams);
        setAreaSummary(area);
      } else {
        setAreaSummary(null);
      }
    } catch (error) {
      setErrorMessage(error.message || "Unable to load weather intelligence dashboard.");
    } finally {
      setLoading(false);
    }
  }, [queryParams, timelineEventFilter, municipality]);

  useEffect(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    debounceRef.current = setTimeout(loadDashboard, 350);
    return () => clearTimeout(debounceRef.current);
  }, [loadDashboard]);

  const openIncident = async (type, id) => {
    setIncidentLoading(true);
    try {
      const detail = await getWeatherDashboardIncident(type, id);
      setIncident(detail);
    } catch (error) {
      setErrorMessage(error.message || "Unable to load incident detail.");
    } finally {
      setIncidentLoading(false);
    }
  };

  const handleTimelineClick = (item) => {
    const target = item.linkTarget;
    if (!target?.id) {
      return;
    }

    if (target.resource === "midpoint_alert") {
      openIncident("midpoint_alert", target.id);
    } else if (target.resource === "official_alert") {
      openIncident("official_alert", target.id);
    } else if (target.resource === "community_report") {
      openIncident("community_report", target.id);
    } else if (target.resource === "storm_cell") {
      openIncident("storm_cell", target.id);
    }
  };

  const viewOnMap = (focusType, focusId) => {
    setMapFocusHint({ focusType, focusId });
    if (onOpenMap) {
      onOpenMap();
    } else {
      window.location.hash = "";
    }
  };

  const cards = summary?.cards || {};

  return (
    <div className="midpoint-app weather-dashboard-app">
      <header className="midpoint-header">
        <div className="midpoint-header-inner admin-moderation-header">
          <div>
            <p className="eyebrow">Operations</p>
            <h1 className="admin-moderation-title">Weather Intelligence Dashboard</h1>
            <p className="muted">{DISCLAIMER}</p>
          </div>
          <div className="weather-dashboard-header-actions">
            <button type="button" className="midpoint-share-button" onClick={loadDashboard}>
              Refresh
            </button>
            <button type="button" className="midpoint-share-button" onClick={onClose}>
              Back to map
            </button>
          </div>
        </div>
      </header>

      <div className="weather-dashboard-controls panel-card">
        <label>
          Time window
          <select value={windowCode} onChange={(e) => setWindowCode(e.target.value)}>
            {WINDOW_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </label>
        <label>
          Municipality scope
          <input
            value={municipality}
            onChange={(e) => setMunicipality(e.target.value)}
            placeholder="e.g. Medellín"
          />
        </label>
        <label className="toggle-row">
          <input
            type="checkbox"
            checked={includeSample}
            onChange={(e) => setIncludeSample(e.target.checked)}
          />
          <span>Include sample/demo data</span>
        </label>
        <label className="toggle-row">
          <input
            type="checkbox"
            checked={sampleOnly}
            onChange={(e) => setSampleOnly(e.target.checked)}
          />
          <span>Sample/demo only</span>
        </label>
        {summary?.generatedAt ? (
          <p className="muted small">Generated {formatTime(summary.generatedAt)}</p>
        ) : null}
      </div>

      {errorMessage ? <p className="layer-error panel-card">{errorMessage}</p> : null}
      {loading ? <p className="panel-card muted">Loading dashboard…</p> : null}

      {!loading && summary ? (
        <>
          <section className="weather-dashboard-cards">
            {[
              ["activeMidpointAlerts", "Midpoint alerts", "midpoint"],
              ["activeOfficialAlerts", "Official alerts", "official"],
              ["newCommunityReports", "New community reports", "community"],
              ["verifiedCommunityReports", "Verified reports", "community"],
              ["activeStormCells", "Active storm cells", "storm"],
              ["elevatedHydrologySensors", "Elevated stream sensors", "hydrology"],
              ["maxRainfall1HourMm", "Max rainfall (1h)", "rainfall"],
              ["maxRainfall3HourMm", "Max rainfall (3h)", "rainfall"],
              ["highRiskFloodZonesAffected", "High-risk flood zones", "flood"],
            ].map(([key, label, listKey]) => (
              <button
                key={key}
                type="button"
                className="weather-dashboard-card"
                onClick={() => setActiveList(listKey)}
              >
                <span className="weather-dashboard-card-value">
                  {cards[key] ?? "—"}
                </span>
                <span className="weather-dashboard-card-label">{label}</span>
              </button>
            ))}
            <div className="weather-dashboard-card weather-dashboard-card--static">
              <span className="weather-dashboard-card-value">
                {cards.latestRadarFrameAt ? formatTime(cards.latestRadarFrameAt) : "—"}
              </span>
              <span className="weather-dashboard-card-label">Latest radar frame</span>
            </div>
          </section>

          {areaSummary ? (
            <section className="panel-card">
              <h3>Area summary — {areaSummary.area?.areaName}</h3>
              <ul className="area-summary-metrics">
                <li>Midpoint alerts: {areaSummary.summary?.midpointAlertCount}</li>
                <li>Official alerts: {areaSummary.summary?.officialAlertCount}</li>
                <li>Community reports: {areaSummary.summary?.communityReportCount}</li>
                <li>Max rainfall: {areaSummary.summary?.maxRainfallMm ?? "—"} mm</li>
              </ul>
              {areaSummary.topConcerns?.length ? (
                <ul>
                  {areaSummary.topConcerns.map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
              ) : null}
            </section>
          ) : null}

          <section className="panel-card weather-dashboard-ranking">
            <h3>Risk ranking</h3>
            {ranking?.message ? <p className="muted">{ranking.message}</p> : null}
            {ranking?.rankedAreas?.length ? (
              <table className="weather-dashboard-table">
                <thead>
                  <tr>
                    <th>Rank</th>
                    <th>Area</th>
                    <th>Risk</th>
                    <th>Score</th>
                    <th>Summary</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {ranking.rankedAreas.map((row) => (
                    <tr key={row.areaId}>
                      <td>{row.rank}</td>
                      <td>
                        {row.areaName}
                        {row.isSampleData ? (
                          <span className="sample-badge"> sample</span>
                        ) : null}
                      </td>
                      <td>
                        <span className={riskLevelClass(row.riskLevel)}>{row.riskLevel}</span>
                      </td>
                      <td>{row.riskScore}</td>
                      <td>{row.summary}</td>
                      <td>
                        <button
                          type="button"
                          className="secondary-btn"
                          onClick={() =>
                            getWeatherDashboardAreaSummary({
                              ...queryParams,
                              floodRiskZoneId: row.areaId,
                            }).then(setAreaSummary)
                          }
                        >
                          Area
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <p className="muted">No ranked areas for current scope.</p>
            )}
            <p className="muted small">{ranking?.disclaimer}</p>
          </section>

          <section className="panel-card weather-dashboard-timeline">
            <div className="timeline-header">
              <h3>Unified timeline ({timeline?.total || 0})</h3>
              <select
                value={timelineEventFilter}
                onChange={(e) => setTimelineEventFilter(e.target.value)}
              >
                <option value="">All event types</option>
                <option value="MIDPOINT_ALERT">Midpoint alerts</option>
                <option value="OFFICIAL_ALERT">Official alerts</option>
                <option value="COMMUNITY_REPORT">Community reports</option>
                <option value="STORM_OBSERVATION">Storm</option>
                <option value="RAINFALL_SNAPSHOT">Rainfall</option>
                <option value="HYDROLOGY_READING">Hydrology</option>
                <option value="ALERT_CORRELATION">Correlations</option>
              </select>
            </div>
            <ul className="weather-timeline-list">
              {(timeline?.items || []).map((item) => (
                <li key={item.eventKey}>
                  <button
                    type="button"
                    className="weather-timeline-item"
                    onClick={() => handleTimelineClick(item)}
                  >
                    <span className="weather-timeline-time">{formatTime(item.eventTime)}</span>
                    <span className="weather-timeline-type">{item.eventType}</span>
                    <strong>{item.title}</strong>
                    {item.isSampleData ? <span className="sample-badge"> demo</span> : null}
                    <p className="muted small">{item.description}</p>
                  </button>
                </li>
              ))}
            </ul>
          </section>

          {activeList ? (
            <section className="panel-card">
              <h3>Active intelligence — {activeList}</h3>
              <p className="muted small">
                Filter timeline by type or click items for incident detail.
              </p>
              <button type="button" className="secondary-btn" onClick={() => setActiveList(null)}>
                Close list
              </button>
            </section>
          ) : null}
        </>
      ) : null}

      {incident || incidentLoading ? (
        <div className="weather-incident-drawer panel-card">
          <div className="weather-incident-drawer-header">
            <h3>Incident detail</h3>
            <button type="button" onClick={() => setIncident(null)}>
              Close
            </button>
          </div>
          {incidentLoading ? <p className="muted">Loading…</p> : null}
          {incident?.type === "midpoint_alert" ? (
            <>
              <p className="popup-warning">{incident.disclaimer}</p>
              <h4>{incident.alert?.title}</h4>
              <p>{incident.alert?.description}</p>
              <p>
                Severity: {incident.alert?.severity} · Status: {incident.alert?.status}
              </p>
              <button
                type="button"
                className="secondary-btn"
                onClick={() =>
                  viewOnMap("midpoint_alert", incident.alert?.weather_alert_id)
                }
              >
                View on map
              </button>
              <p>Correlations: {incident.correlations?.length || 0}</p>
            </>
          ) : null}
          {incident?.type === "official_alert" ? (
            <>
              <h4>{incident.alert?.alert_title}</h4>
              <p>{incident.attribution}</p>
              <p>Correlations: {incident.correlations?.length || 0}</p>
              <button
                type="button"
                className="secondary-btn"
                onClick={() =>
                  viewOnMap("official_alert", incident.alert?.official_weather_alert_id)
                }
              >
                View on map
              </button>
            </>
          ) : null}
          {incident?.type === "community_report" ? (
            <>
              <h4>{incident.report?.title}</h4>
              <p>{incident.unverifiedLabel}</p>
              <p>Status: {incident.report?.status}</p>
              <button
                type="button"
                className="secondary-btn"
                onClick={() =>
                  viewOnMap("community_report", incident.report?.community_report_id)
                }
              >
                View on map
              </button>
            </>
          ) : null}
          {incident?.type === "storm_cell" ? (
            <>
              <h4>Storm {incident.stormCell?.storm_cell_key}</h4>
              <p>Observations: {incident.observations?.length || 0}</p>
              <p>Projections: {incident.projections?.length || 0}</p>
              <button
                type="button"
                className="secondary-btn"
                onClick={() => viewOnMap("storm_cell", incident.stormCell?.storm_cell_id)}
              >
                View on map
              </button>
            </>
          ) : null}
          {incident?.timeline?.length ? (
            <ul className="weather-timeline-list compact">
              {incident.timeline.slice(0, 8).map((item) => (
                <li key={item.eventKey}>
                  {formatTime(item.eventTime)} — {item.title}
                </li>
              ))}
            </ul>
          ) : null}
        </div>
      ) : null}
    </div>
  );
};

export default WeatherIntelligenceDashboardPage;
